/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";

type Props = {
  weeks: { week: string; total: number }[];
  /** What the bars are, for screen readers. */
  label: string;
};

const weekLabel = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });

// A total per week (purchases, sales): one series, thin bars on a baseline,
// value on hover.
export const WeeklyBars = defineComponent<Props>({
  name: "WeeklyBars",
  props: ["weeks", "label"],
  setup(props) {
    const { t } = useLocalization();
    const hover = ref<number | null>(null);

    return () => {
      const max = Math.max(...props.weeks.map((w) => w.total), 0);
      const last = props.weeks.length - 1;
      return (
        <div>
          <div class="relative flex h-36 items-end gap-2 border-b border-base-300" role="img" aria-label={props.label}>
            {props.weeks.map((w, i) => {
              const pct = max > 0 ? Math.max((w.total / max) * 100, w.total > 0 ? 2 : 0) : 0;
              return (
                <div
                  key={w.week}
                  class="relative flex h-full flex-1 cursor-default items-end justify-center"
                  onMouseenter={() => (hover.value = i)}
                  onMouseleave={() => (hover.value = null)}
                  aria-label={`${weekLabel(w.week)}: ${w.total.toFixed(2)}`}
                >
                  <div
                    class={[
                      "w-full max-w-8 rounded-t bg-primary transition-opacity",
                      hover.value != null && hover.value !== i ? "opacity-40" : i === last ? "opacity-100" : "opacity-75",
                    ].join(" ")}
                    style={{ height: `${pct}%` }}
                  />
                  {hover.value === i && (
                    <div class="pointer-events-none absolute bottom-full z-10 mb-1 whitespace-nowrap rounded-md bg-neutral px-2 py-1 text-xs text-neutral-content shadow">
                      <div class="text-neutral-content/70">{t("inventory.overview.week_of").replace("{date}", weekLabel(w.week))}</div>
                      <div class="font-semibold tabular-nums">{w.total.toFixed(2)}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div class="mt-1 flex gap-2 text-[10px] text-base-content/50">
            {props.weeks.map((w, i) => (
              <div key={w.week} class="flex-1 text-center">
                {i % 2 === last % 2 ? weekLabel(w.week) : ""}
              </div>
            ))}
          </div>
        </div>
      );
    };
  },
});
